import {
    useCallback,
    useState,
    type FormEventHandler,
    type ReactNode
} from 'react';
import { DictationBusyContext } from './dictationBusy';
import Box from '@mui/material/Box';
import FormDialog from './FormDialog';
import FormDialogFooter from './FormDialogFooter';
import { FormSectionsBody } from './FormSection';
import { readOnlySheetSx } from './formFields';
import { FormReadOnlyContext } from './formReadOnly';
import { FormSectionNavProvider } from './formSectionNav';
import { type FormSection } from './formSectionNavContext';
import UnsavedChangesDialog from './UnsavedChangesDialog';
import {
    RecordTranslationsContext,
    type RecordTranslations
} from '../features/translation/recordTranslations';
import type { useUnsavedCloseGuard } from './useUnsavedCloseGuard';
import type { useGuardedNavigate } from './useGuardedNavigate';

/**
 * The frame every fullscreen record editor is built in: the dialog, the jump-to-
 * section nav around it, the `<form>` that owns submitting, the scrolling body
 * and the sticky footer — plus the two unsaved-changes guards, which are part of
 * the frame rather than of any one form.
 *
 * All eight record dialogs had written this out themselves, identically down to
 * the flex column and the order of the guards. What differs is the app bar and
 * the body, so those come in as nodes.
 *
 * The footer's Save is the form's submit — it sits INSIDE the `<form>`, so
 * Ctrl/Cmd+Enter saves too (see FormDialog; a plain Enter moves on).
 */
export default function RecordFormDialog({
    open,
    sections,
    onSubmit,
    closeGuard,
    navGuard,
    appBar,
    isSubmitting,
    footerStartSlot,
    extraDialogs,
    readOnly = false,
    translations = null,
    children
}: {
    open: boolean;
    /** The sections the nav rail offers — see formSectionNav. */
    sections: FormSection[];
    /** `handleSubmit(onSubmit)` of the form's own react-hook-form. */
    onSubmit: FormEventHandler<HTMLFormElement>;
    /** Guards closing (Esc, X, Abbrechen, browser back). */
    closeGuard: ReturnType<typeof useUnsavedCloseGuard>;
    /** Guards leaving for another record ("save and go"). */
    navGuard: ReturnType<typeof useGuardedNavigate>;
    /** The record's own `<FormDialogAppBar>` — title, crumbs, section menu. */
    appBar: ReactNode;
    isSubmitting: boolean;
    /** An action about the RECORD rather than the editing ("als PDF"). */
    footerStartSlot?: ReactNode;
    /**
     * Dialogs of the record's own that must live outside the form — the Befund's
     * PDF progress and its size confirmation. They belong after the guards, so
     * a guard asking about unsaved edits is never covered by them.
     */
    extraDialogs?: ReactNode;
    /**
     * The reader may see the record but not change it: Save is off and the form
     * does not submit. The fields turn read-only through `bindFormFields`, the
     * frame draws them as a sheet (see formFields/readOnlySheet).
     */
    readOnly?: boolean;
    /**
     * The record's translations (see useRecordTranslations). Its long-text
     * fields offer translating only inside one; the form commits it on save.
     */
    translations?: RecordTranslations | null;
    children: ReactNode;
}) {
    /**
     * The backstop for a reader: whatever text input a dialog renders by hand
     * and forgot to make read-only still takes no typing, pasting or dropping.
     * `beforeinput` is the one event every edit of an input, a textarea and a
     * contenteditable passes through, and it can be cancelled.
     *
     * A callback ref, not an effect: the form lives in the Dialog's portal,
     * which mounts its content only after the first commit, so an effect would
     * find no form and never run again.
     */
    const formRef = useCallback(
        (form: HTMLFormElement | null) => {
            if (!readOnly || !form) {
                return;
            }
            const block = (event: Event) => event.preventDefault();
            form.addEventListener('beforeinput', block, true);
            return () => form.removeEventListener('beforeinput', block, true);
        },
        [readOnly]
    );

    /**
     * How many of the form's fields a dictation is writing into. Saving waits
     * for them: they hold DeepL's provisional words (see dictationBusy).
     */
    const [dictating, setDictating] = useState(0);
    const reportDictation = useCallback(
        (busy: boolean) => setDictating((count) => count + (busy ? 1 : -1)),
        []
    );
    const blocked = readOnly || dictating > 0;

    return (
        <FormDialog open={open} onClose={closeGuard.requestClose} fullScreen>
            <FormSectionNavProvider sections={sections}>
                <Box
                    component="form"
                    ref={formRef}
                    onSubmit={
                        // Strg+Enter submits through here too (requestSubmit).
                        blocked ? (event) => event.preventDefault() : onSubmit
                    }
                    sx={[
                        {
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%'
                        },
                        readOnly && readOnlySheetSx
                    ]}
                >
                    {appBar}

                    <FormReadOnlyContext.Provider value={readOnly}>
                        <RecordTranslationsContext.Provider
                            value={translations}
                        >
                            <DictationBusyContext.Provider
                                value={reportDictation}
                            >
                                <FormSectionsBody>{children}</FormSectionsBody>
                            </DictationBusyContext.Provider>
                        </RecordTranslationsContext.Provider>
                    </FormReadOnlyContext.Provider>

                    <FormDialogFooter
                        onCancel={closeGuard.requestClose}
                        isSubmitting={isSubmitting}
                        saveDisabled={blocked}
                        startSlot={footerStartSlot}
                    />
                </Box>
            </FormSectionNavProvider>
            {/* Two guards, two dialogs, in this order: the close guard is the
                one the user reaches by Esc or Abbrechen, the nav guard only by
                following a link out of the record. */}
            <UnsavedChangesDialog {...closeGuard.confirmProps} />
            <UnsavedChangesDialog {...navGuard.confirmProps} />
            {extraDialogs}
        </FormDialog>
    );
}
